import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  Box,
  Container,
  Grid,
  Paper,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  useTheme,
  useMediaQuery,
  Chip,
  Stack,
  Fade
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  ExpandMore as ExpandMoreIcon,
  SearchOutlined as SearchIcon,
  MapOutlined as MapIcon,
  ViewList as ViewListIcon,
  AutoAwesome as MagicIcon,
  ShoppingCart as ShoppingCartIcon,
  HomeWork as HomeWorkIcon
} from '@mui/icons-material';
import { useSearch } from '../../context/SearchContext';
import { useTranslation } from 'react-i18next';
import IntelligentSearchBar from './IntelligentSearchBar';
import SearchResultsList from './SearchResultsList';
import InteractiveSearchMap from './InteractiveSearchMap';

const EnhancedSearchInterface = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation();
  const resultsRef = useRef(null);
  
  const {
    suggestions,
    results,
    hasResults,
    isLoading,
    error,
    searchType,
    filters,
    updateSearchType,
    performSearch,
    isSearchActive
  } = useSearch();

  const activeTab = searchType || 'listings';

  const handleTabChange = useCallback((value) => {
    updateSearchType(value);

    // Si une recherche est déjà active (ou des résultats sont affichés),
    // on relance une recherche cohérente avec le bon searchType.
    // On passe l'override pour éviter la fenêtre où state.searchType est encore l'ancien.
    if (isSearchActive || hasResults) {
      performSearch({ forceRefresh: true, searchType: value, filters });
    }
  }, [updateSearchType, performSearch, isSearchActive, hasResults]);

  // États pour les accordéons
  const [expandedPanels, setExpandedPanels] = useState({
    suggestions: false,
    history: false
  });
  const [mobilePanel, setMobilePanel] = useState('list');

  const suggestionCount = suggestions?.length || 0;

  // Gestion des accordéons
  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedPanels(prev => ({
      ...prev,
      [panel]: isExpanded
    }));
  };

  // Auto-fermer les accordéons quand on a des résultats (mobile)
  useEffect(() => {
    if (hasResults && isMobile) {
      setExpandedPanels({
        suggestions: false,
        history: false
      });
    }
  }, [hasResults, isMobile]);

  useEffect(() => {
    if (!hasResults) {
      setMobilePanel('list');
    }
  }, [hasResults]);

  // Scroll vers les résultats dès qu'ils apparaissent suite à une navigation/redirection
  const prevHasResults = useRef(false);
  useEffect(() => {
    if (hasResults && !prevHasResults.current && resultsRef.current) {
      resultsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    prevHasResults.current = hasResults;
  }, [hasResults]);

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 1.5, md: 2.5 } }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

        {/* 0. Top shell sticky, ergonomie type Airbnb */}
        <Box
          sx={{
            position: 'sticky',
            top: { xs: 8, md: 12 },
            zIndex: 20,
            display: 'flex',
            flexDirection: 'column',
            gap: 1.25,
            pb: 0.5,
            // removed backdrop blur to keep background transparent
            backdropFilter: 'none'
          }}
        >
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Box
            sx={{
              display: 'inline-flex',
              p: '6px',
              // Container: rectangle with rounded corners (not a pill)
              borderRadius: '12px',
              // Use a solid background (non-transparent)
              background: theme.palette.background.paper,
              border: '1px solid #e9e9e9',
              boxShadow: '0 8px 32px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.08)' 
            }}
          >
            {[
              { value: 'listings', label: t('search.type.listings', 'Locations & Services'), Icon: HomeWorkIcon, color: theme.palette.primary.main },
              { value: 'products', label: t('search.type.products', 'Produits'), Icon: ShoppingCartIcon, color: theme.palette.success.main }
            ].map(({ value, label, Icon, color }) => {
              const active = activeTab === value;
              return (
                <Box
                  key={value}
                  onClick={() => handleTabChange(value)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    px: { xs: 2, md: 3 },
                    py: { xs: 0.75, md: 0.9 },
                    // Button: small rounded rectangle
                    borderRadius: '8px',
                    cursor: 'pointer',
                    userSelect: 'none',
                    transition: 'all 0.22s cubic-bezier(0.4, 0, 0.2, 1)',
                    border: '1.5px solid transparent',
                    ...(active ? {
                      background: `linear-gradient(135deg, ${alpha(color, 0.12)} 0%, ${alpha(color, 0.2)} 100%)`,
                      borderColor: alpha(color, 0.35),
                      boxShadow: `0 2px 14px ${alpha(color, 0.18)}, inset 0 1px 0 ${alpha(theme.palette.common.white, 0.35)}`
                    } : {
                      '&:hover': {
                        // Hover uses a green background similar to available calendar days
                        background: `linear-gradient(90deg, ${alpha(theme.palette.success.main, 0.08)} 0%, ${alpha(theme.palette.success.main, 0.14)} 100%)`,
                        borderColor: alpha(theme.palette.success.main, 0.18),
                        boxShadow: `0 6px 18px ${alpha(theme.palette.success.main, 0.06)}`,
                        transform: 'translateY(-2px)',
                        // Slight lift and shadow like calendar-day hover
                        '& svg': { color: theme.palette.success.dark },
                        '& p, & span': { color: theme.palette.success.dark }
                      }
                    })
                  }}
                >
                  <Icon sx={{
                    fontSize: { xs: 17, md: 18 },
                    color: active ? color : 'text.secondary',
                    transition: 'color 0.2s'
                  }} />
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: active ? 700 : 400,
                      color: active ? color : 'text.secondary',
                      fontSize: { xs: '0.775rem', md: '0.875rem' },
                      letterSpacing: active ? 0.15 : 0,
                      transition: 'all 0.2s',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {label}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* 1. BARRE DE RECHERCHE PRINCIPALE */}
        <Paper 
          elevation={0}
          sx={{ 
            p: { xs: 1.5, md: 2 },
            borderRadius: 999,
            background: '#fff',
            border: '1px solid #e9e9e9',
            boxShadow: '0 6px 24px rgba(0,0,0,0.08)'
          }}
        >
          <IntelligentSearchBar />
        </Paper>
        </Box>

        {/* 2. ACCORDÉONS POUR SUGGESTIONS */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>

          {/* Accordéon Suggestions Intelligentes */}
          {suggestionCount > 0 && (
            <Accordion 
              expanded={expandedPanels.suggestions} 
              onChange={handleAccordionChange('suggestions')}
              elevation={2}
              sx={{
                borderRadius: 2,
                '&:before': { display: 'none' },
                boxShadow: theme.shadows[2],
                background: `linear-gradient(135deg, ${theme.palette.secondary.main}08 0%, ${theme.palette.primary.main}10 30%, ${theme.palette.background.paper} 70%)`,
                border: `1px solid ${theme.palette.secondary.main}20`
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  backgroundColor: `${theme.palette.secondary.main}08`,
                  borderRadius: expandedPanels.suggestions ? '8px 8px 0 0' : '8px',
                  '& .MuiAccordionSummary-content': {
                    alignItems: 'center',
                    gap: 2
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                  <MagicIcon color="secondary" />
                  <Typography variant="h6" fontWeight={600}>
                    {t('search.suggestions.title', 'Suggestions Intelligentes')}
                  </Typography>
                  <Chip 
                    label={`${suggestionCount} suggestion${suggestionCount > 1 ? 's' : ''}`}
                    size="small"
                    color="secondary"
                    variant="outlined"
                  />
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 3 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Suggestions basées sur votre recherche et vos préférences
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                  {suggestions.slice(0, 8).map((suggestion, index) => (
                    <Chip
                      key={index}
                      label={suggestion.text}
                      variant="outlined"
                      clickable
                      size="medium"
                      sx={{
                        borderRadius: 2,
                        '&:hover': {
                          backgroundColor: theme.palette.secondary.main + '20'
                        }
                      }}
                    />
                  ))}
                </Stack>
              </AccordionDetails>
            </Accordion>
          )}

        </Box>

        {/* 3. INTERFACE PRINCIPALE - LISTINGS ET CARTE */}
        <Fade in={hasResults} timeout={300}>
          <Box ref={resultsRef} sx={{ display: hasResults ? 'block' : 'none', mb: 4 }}>
            {isMobile && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1.25 }}>
                <Paper
                  elevation={0}
                  sx={{
                    display: 'inline-flex',
                    p: 0.5,
                    borderRadius: 999,
                    border: '1px solid #e9e9e9',
                    background: '#fff'
                  }}
                >
                  <Button
                    size="small"
                    startIcon={<ViewListIcon />}
                    variant={mobilePanel === 'list' ? 'contained' : 'text'}
                    onClick={() => setMobilePanel('list')}
                    sx={{ borderRadius: 999, textTransform: 'none' }}
                  >
                    {t('search.view.list')}
                  </Button>
                  <Button
                    size="small"
                    startIcon={<MapIcon />}
                    variant={mobilePanel === 'map' ? 'contained' : 'text'}
                    onClick={() => setMobilePanel('map')}
                    sx={{ borderRadius: 999, textTransform: 'none' }}
                  >
                    {t('search.view.map')}
                  </Button>
                </Paper>
              </Box>
            )}

            <Grid container spacing={2} sx={{ height: 'auto', position: 'relative', zIndex: 1 }}>
              {(mobilePanel === 'list' || !isMobile) && (
              <Grid item xs={12} md={7}>
                <Paper 
                  elevation={0}
                  sx={{ 
                    height: 'auto',
                    minHeight: { xs: '62vh', md: 'unset' },
                    borderRadius: 4,
                    overflow: 'visible',
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    zIndex: 2,
                    background: '#fff',
                    border: '1px solid #ececec'
                  }}
                >
                  {/* Contenu de la liste */}
                  <Box sx={{ flex: 1, overflow: 'visible' }}>
                    <SearchResultsList />
                  </Box>
                </Paper>
              </Grid>
              )}

              {/* Colonne de droite - Carte interactive */}
              {(mobilePanel === 'map' || !isMobile) && (
              <Grid item xs={12} md={5}>
                <Paper 
                  elevation={0}
                  sx={{ 
                    height: { xs: '100vh', md: '105vh' },
                    borderRadius: 4,
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    background: '#fff',
                    border: '1px solid #ececec',
                    position: { xs: 'relative', md: 'sticky' },
                    top: { md: 0 }
                  }}
                >
                  {/* En-tête de la carte - Simplifié 
                  <Box 
                    sx={{ 
                      p: 1.25,
                      backgroundColor: '#fff',
                      borderBottom: `1px solid ${theme.palette.divider}`,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}
                  >
                    <MapIcon sx={{ color: '#ff385c' }} />
                    <Typography variant="subtitle1" fontWeight={700}>
                      Carte Interactive
                    </Typography>
                  </Box>
                  */}
                  {/* Contenu de la carte */}
                  <Box sx={{ flex: 1, overflow: 'hidden' }}>
                    <InteractiveSearchMap />
                  </Box>
                </Paper>
              </Grid>
              )}

            </Grid>
          </Box>
        </Fade>

        {/* Message quand pas de résultats */}
        {!hasResults && !isLoading && (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <SearchIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h5" color="text.secondary" gutterBottom>
              {t('search.noResults.title', 'Commencez votre recherche')}
            </Typography>
            <Typography variant="body1" color="text.disabled">
              {t('search.noResults.subtitle', 'Utilisez la barre de recherche pour trouver des équipements')}
            </Typography>
          </Box>
        )}

      </Box>
    </Container>
  );
};

export default EnhancedSearchInterface;
